import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Button, Modal } from "antd";
import "./style.css";

import "mathlive";
import { InlineMath } from "react-katex";
import { splitTextAndLatex } from "general_function";
import UploadImgs from "./UploadImgs";
import { CalculatorOutlined } from "@ant-design/icons";

function LatexMath({ math }) {
    const ref = useRef();
    useEffect(() => {
        var firstChild = ref.current;
        while (firstChild) {
            if (firstChild.nodeType === Node.TEXT_NODE) {
                break;
            }
            firstChild.setAttribute("latex-content", `$${math}$`);
            firstChild = firstChild.firstChild;
        }
    },[math])
    return <span contentEditable="false" latex-content={`$${math}$`} ref={ref}>
        <InlineMath math={math} />
    </span>
}

function RickTextRender({ message }) {
    return message.map((m, index) => (
        <React.Fragment key={index}>
            {m.type === "text" ? (
                <span text-type="text" style={{ whiteSpace: "pre-line" }}>{m.content}</span>
            ) : (
                <LatexMath math={m.content}/>
            )}
        </React.Fragment>
    ));
}

function MathField({ value, setValue }) {
    const mfRef = useRef(null);

    useEffect(() => {
        const mf = mfRef.current;
        if (!mf) return;

        const handleMount = () => {
            mf.smartFence = true;
            mf.smartMode = true;
            mf.mathVirtualKeyboardPolicy = "off";
        };

        mf.addEventListener("mount", handleMount);

        return () => {
            mf.removeEventListener("mount", handleMount);
        };
    }, []);

    useEffect(() => {
        const mf = mfRef.current;
        if (!mf) return;

        const handleInput = () => {
            setValue(mf.value);
        };

        mf.addEventListener("input", handleInput);

        return () => {
            mf.removeEventListener("input", handleInput);
        };
        // eslint-disable-next-line
    }, []);

    return (
        <math-field
            ref={mfRef}
            style={{ width: "100%", minHeight: "50px", fontSize: "18px" }}
            math-virtual-keyboard-policy="off"
        >
            {value}
        </math-field>
    );
}

export default function RichEditor({ value, setValue }) {
    const textRef = useRef(null);
    const [openMath, setOpenMath] = useState(false);
    const [math, setMath] = useState("");
    // const [pointerPosition, setPointerPosition] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            const mf = document.querySelector("math-field");
            if (!mf) return 
            clearInterval(interval)
            var sheet = new CSSStyleSheet();
            sheet.replaceSync(
                `div[role="presentation"] > .ui-menu-container:nth-child(1) > li:nth-child(n + 4) { display: none; }`
            );
            mf.shadowRoot.adoptedStyleSheets.push(sheet);
        }, 100);
    }, []);

    const formatMessage = useMemo(
        () => splitTextAndLatex(value.text),
        [value.text]
    );

    const handleImageList = useCallback(
        (lists) => {
            setValue({ ...value, images: lists });
        },
        [value, setValue]
    );

    return (
        <>
            <div style={{ position: "relative" }}>
                <div
                    id="rich-editor"
                    key={value.text}
                    contentEditable
                    data-placeholder="Nhập..."
                    suppressContentEditableWarning={true}
                    onBlur={(e) => {
                        var newValue = "";
                        e.target.innerHTML = e.target.innerHTML.replaceAll("<br>", "\n");
                        const childNodes = Array.from(e.target.childNodes);
                        for (const element of childNodes) {
                            if (element.nodeType !== Node.TEXT_NODE) {
                                const isLatex = element.getAttribute("text-type") === "latex";
                                const isMathML = element.getAttribute("class") === "katex-mathml";

                                if (isLatex || isMathML) continue;
                                const innerNodes = Array.from(element.childNodes);
                                if (element.nodeName  === "DIV") {
                                    element.parentNode.insertBefore(document.createTextNode("\n"), element);
                                }
                                for (const child of innerNodes) {
                                    element.parentNode.insertBefore(child, element);
                                }

                                element.remove();
                            }
                        }
                        for (const element of e.target.childNodes) {
                            if (
                                (element.nodeType === Node.TEXT_NODE) ||
                                element.getAttribute("text-type") === "text"
                            ) {
                                newValue += element.textContent;
                            } else if (
                                element.getAttribute("latex-content") !== null
                            ) {
                                newValue +=
                                    element.getAttribute("latex-content");
                            } else if (element.getAttribute("aria-hidden") !== "true") {
                                newValue += element.textContent;
                            }
                        }
                        setValue({
                            ...value,
                            text: newValue,
                        });
                    }}
                    ref={textRef}
                >
                    <RickTextRender message={formatMessage} />
                </div>
                <Button
                    onClick={() => {
                        setOpenMath(true);
                        setMath("");
                    }}
                    type="primary"
                    style={{ position: "absolute", right: 0, top: 0 }}
                >
                    <CalculatorOutlined />
                </Button>
            </div>
            <UploadImgs fileList={value.images} setFileList={handleImageList} />
            <Modal
                open={openMath}
                forceRender
                onCancel={() => setOpenMath(false)}
                title="Thêm phép toán"
                onOk={() => {
                    setOpenMath(false);
                    setValue({
                        ...value,
                        text: value.text + `$${math}$`,
                    });
                }}
            >
                <MathField value={math} setValue={setMath} />
            </Modal>
        </>
    );
}
